import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  Textarea,
  Switch,
  Select,
  IconButton,
  Alert,
  AlertIcon,
  Spinner,
  useColorModeValue,
  SimpleGrid,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaTrash, FaPlus, FaGripVertical } from 'react-icons/fa';
import { createFormAdmin, getFormAdmin, updateFormAdmin } from '../services/formService';

const emptyField = (type = 'text') => ({
  key: `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  type,
  label: '',
  required: false,
  options: type === 'select' ? ['گزینه ۱', 'گزینه ۲'] : [],
});

function SelectOptionsEditor({ options, onChange, borderColor }) {
  const muted = useColorModeValue('gray.500', 'gray.400');
  const rowBg = useColorModeValue('gray.50', 'gray.600');

  const addOption = () => {
    const next = [...(options || []), `گزینه ${(options?.length || 0) + 1}`];
    onChange(next);
  };

  const updateOption = (idx, value) => {
    const next = [...(options || [])];
    next[idx] = value;
    onChange(next);
  };

  const removeOption = (idx) => {
    const next = (options || []).filter((_, i) => i !== idx);
    onChange(next.length ? next : ['گزینه ۱']);
  };

  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" mb={2}>
        گزینه‌های انتخابی
      </Text>
      <Text fontSize="xs" color={muted} mb={3}>
        هر ردیف یک گزینه در لیست کشویی فرم عمومی است.
      </Text>
      <Stack spacing={2}>
        {(options || []).map((opt, optIdx) => (
          <HStack
            key={`opt-${optIdx}`}
            spacing={2}
            p={2}
            bg={rowBg}
            borderRadius="md"
            borderWidth="1px"
            borderColor={borderColor}
            align="center"
          >
            <Box color={muted} display={{ base: 'none', sm: 'block' }} flexShrink={0}>
              <FaGripVertical size={12} />
            </Box>
            <Input
              flex={1}
              size="md"
              value={opt}
              onChange={(e) => updateOption(optIdx, e.target.value)}
              placeholder={`گزینه ${optIdx + 1}`}
            />
            <IconButton
              aria-label="حذف گزینه"
              icon={<FaTrash />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              flexShrink={0}
              onClick={() => removeOption(optIdx)}
              isDisabled={(options || []).length <= 1}
            />
          </HStack>
        ))}
      </Stack>
      <Button
        mt={3}
        w="100%"
        size="sm"
        variant="outline"
        colorScheme="teal"
        leftIcon={<FaPlus />}
        onClick={addOption}
      >
        افزودن گزینه
      </Button>
    </Box>
  );
}

export default function FormEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const textColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const stickyBarBg = useColorModeValue('rgba(255,255,255,0.97)', 'rgba(45,55,72,0.97)');

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [completionMessage, setCompletionMessage] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    if (isNew) {
      setFields([emptyField('text')]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getFormAdmin(id);
        const f = res.data?.data?.form;
        if (!f) throw new Error('یافت نشد');
        if (cancelled) return;
        setTitle(f.title || '');
        setDescription(f.description || '');
        setCompletionMessage(f.completionMessage || '');
        setIsActive(f.isActive !== false);
        setFields(
          (f.fields || []).length
            ? f.fields.map((x) => ({
                ...x,
                options: x.options || [],
              }))
            : [emptyField('text')]
        );
      } catch {
        toast.error('بارگذاری فرم ناموفق بود');
        navigate('/form-builder');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate]);

  const updateField = (idx, patch) => {
    setFields((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const removeField = (idx) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const addField = (type) => {
    setFields((prev) => [...prev, emptyField(type)]);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('عنوان فرم را وارد کنید');
      return;
    }
    for (let i = 0; i < fields.length; i++) {
      if (!fields[i].label?.trim()) {
        toast.error(`برچسب فیلد ${i + 1} خالی است`);
        return;
      }
      if (fields[i].type === 'select') {
        const opts = (fields[i].options || []).map((o) => String(o).trim()).filter(Boolean);
        if (!opts.length) {
          toast.error(`فیلد انتخابی «${fields[i].label}» باید حداقل یک گزینه داشته باشد`);
          return;
        }
      }
    }

    const payload = {
      title: title.trim(),
      description: description.trim(),
      completionMessage: completionMessage.trim(),
      isActive,
      fields: fields.map((f) => ({
        key: f.key,
        type: f.type,
        label: f.label.trim(),
        required: Boolean(f.required),
        options:
          f.type === 'select'
            ? (f.options || []).map((o) => String(o).trim()).filter(Boolean)
            : [],
      })),
    };

    setSaving(true);
    try {
      if (isNew) {
        const res = await createFormAdmin(payload);
        const newId = res.data?.data?.form?._id;
        toast.success('فرم ایجاد شد');
        navigate(newId ? `/form-builder/${newId}/edit` : '/form-builder');
      } else {
        await updateFormAdmin(id, payload);
        toast.success('ذخیره شد');
        navigate('/form-builder');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'ذخیره ناموفق');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" py={20}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <Box maxW="900px" w="100%" pb={{ base: '6rem', lg: 0 }}>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
        <Heading size="md" color={textColor}>
          {isNew ? 'فرم جدید' : 'ویرایش فرم'}
        </Heading>
        <Button as={Link} to="/form-builder" variant="outline" colorScheme="gray">
          بازگشت
        </Button>
      </Flex>

      <Alert status="info" borderRadius="md" mb={6} fontSize="sm">
        <AlertIcon />
        در فرم‌های عمومی، شمارهٔ موبایل فرد پس از تأیید پیامکی به‌صورت خودکار در کنار پاسخ‌ها ذخیره
        می‌شود. فقط فیلدهای اضافهٔ مورد نیاز خود را در پایین تعریف کنید.
      </Alert>

      <Stack spacing={6} bg={cardBg} p={{ base: 4, md: 6 }} borderRadius="xl" boxShadow="sm">
        <FormControl w="100%">
          <FormLabel>عنوان فرم</FormLabel>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </FormControl>
        <FormControl w="100%">
          <FormLabel>توضیحات (اختیاری)</FormLabel>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </FormControl>
        <FormControl w="100%">
          <FormLabel>متن پس از ثبت پاسخ (اختیاری)</FormLabel>
          <Textarea
            value={completionMessage}
            onChange={(e) => setCompletionMessage(e.target.value)}
            rows={4}
            placeholder="در صورت خالی بودن، پیام پیش‌فرض «پاسخ شما ثبت شد.» نمایش داده می‌شود."
          />
          <Text fontSize="xs" color="gray.500" mt={1}>
            این متن پس از ذخیرهٔ موفق پاسخ به کاربر نشان داده می‌شود؛ می‌توانید چند خط بنویسید.
          </Text>
        </FormControl>
        <FormControl
          w="100%"
          display="flex"
          flexDirection={{ base: 'column', sm: 'row' }}
          alignItems={{ base: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={3}
        >
          <FormLabel mb={{ base: 0, sm: 0 }}>فرم فعال در صفحهٔ عمومی</FormLabel>
          <Switch isChecked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        </FormControl>

        <Divider />

        <Box w="100%">
          <Text fontWeight="bold" mb={3}>
            فیلدها
          </Text>
          <Text fontSize="sm" color="gray.500" mb={3}>
            نوع فیلد جدید را اضافه کنید:
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={2} w="100%">
            <Button w="100%" size="sm" leftIcon={<FaPlus />} onClick={() => addField('text')}>
              متن
            </Button>
            <Button w="100%" size="sm" leftIcon={<FaPlus />} onClick={() => addField('textarea')}>
              متن چندخطی
            </Button>
            <Button w="100%" size="sm" leftIcon={<FaPlus />} onClick={() => addField('phone')}>
              موبایل
            </Button>
            <Button w="100%" size="sm" leftIcon={<FaPlus />} onClick={() => addField('select')}>
              انتخابی
            </Button>
          </SimpleGrid>

          <Stack spacing={4} mt={4}>
            {fields.map((field, idx) => (
              <Box
                key={field.key}
                p={{ base: 3, md: 4 }}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={borderColor}
                w="100%"
              >
                <Flex justify="flex-end" mb={2}>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    icon={<FaTrash />}
                    aria-label="حذف فیلد"
                    onClick={() => removeField(idx)}
                    isDisabled={fields.length === 1}
                  />
                </Flex>

                <Stack spacing={4} w="100%">
                  <FormControl w="100%">
                    <FormLabel fontSize="sm">نوع فیلد</FormLabel>
                    <Select
                      w="100%"
                      value={field.type}
                      onChange={(e) => {
                        const type = e.target.value;
                        updateField(idx, {
                          type,
                          options:
                            type === 'select'
                              ? field.options?.length
                                ? field.options
                                : ['گزینه ۱', 'گزینه ۲']
                              : [],
                        });
                      }}
                    >
                      <option value="text">متن</option>
                      <option value="textarea">چندخطی</option>
                      <option value="phone">موبایل</option>
                      <option value="select">انتخابی</option>
                    </Select>
                  </FormControl>

                  <FormControl w="100%">
                    <FormLabel fontSize="sm">برچسب</FormLabel>
                    <Input
                      w="100%"
                      size="md"
                      value={field.label}
                      onChange={(e) => updateField(idx, { label: e.target.value })}
                      placeholder="مثلاً نام و نام خانوادگی"
                    />
                  </FormControl>

                  <FormControl
                    w="100%"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <FormLabel fontSize="sm" mb={0}>
                      پاسخ الزامی
                    </FormLabel>
                    <Switch
                      isChecked={field.required}
                      onChange={(e) => updateField(idx, { required: e.target.checked })}
                    />
                  </FormControl>

                  {field.type === 'select' && (
                    <SelectOptionsEditor
                      options={field.options}
                      borderColor={borderColor}
                      onChange={(opts) => updateField(idx, { options: opts })}
                    />
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>

      {/* نوار ذخیره — بالای منوی موبایل، تمام‌عرض در موبایل */}
      <Box
        position="sticky"
        bottom={{ base: '4.5rem', lg: 0 }}
        zIndex={900}
        mt={6}
        w="100%"
        mx={{ base: -4, md: 0 }}
        px={{ base: 4, md: 0 }}
        py={4}
        bg={stickyBarBg}
        borderTopWidth={{ base: '1px', lg: 0 }}
        borderColor={borderColor}
        boxShadow={{ base: '0 -4px 16px rgba(0,0,0,0.08)', lg: 'none' }}
        backdropFilter={{ base: 'blur(8px)', lg: 'none' }}
      >
        <Stack spacing={3} w="100%">
          <Button
            w="full"
            size="lg"
            leftIcon={isNew ? <FaPlus /> : undefined}
            colorScheme="teal"
            borderRadius="15px"
            fontWeight="bold"
            onClick={handleSave}
            isLoading={saving}
            loadingText="در حال ذخیره..."
          >
            {isNew ? 'ایجاد فرم' : 'ذخیره تغییرات'}
          </Button>
          <Button
            w="full"
            size="md"
            variant="outline"
            colorScheme="gray"
            as={Link}
            to="/form-builder"
          >
            انصراف
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
